import React, { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '../services/api'

const CategorySelector = ({
  selectedCategories = [],
  onChange,
  multiple = true,
  placeholder = 'Select categories',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const response = await api.get('/categories?active=true')
      return response.data.data
    },
  })

  const filteredCategories = categories.filter((category) =>
    category.translations.some((translation) =>
      translation.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
  )

  const handleCategoryToggle = (category) => {
    if (multiple) {
      const isSelected = selectedCategories.some(
        (cat) => cat.id === category.id
      )
      if (isSelected) {
        onChange(selectedCategories.filter((cat) => cat.id !== category.id))
      } else {
        onChange([...selectedCategories, category])
      }
    } else {
      onChange([category])
      setIsOpen(false)
    }
  }

  const removeCategory = (categoryId) => {
    onChange(selectedCategories.filter((cat) => cat.id !== categoryId))
  }

  const getCategoryName = (category, locale = 'en') => {
    const translation = category.translations.find((t) => t.locale === locale)
    return translation?.name || category.slug
  }

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <div
          className="w-full border border-gray-300 rounded-md px-3 py-2 cursor-pointer bg-white"
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap gap-1">
              {selectedCategories.length === 0 ? (
                <span className="text-gray-500">{placeholder}</span>
              ) : (
                selectedCategories.map((category) => (
                  <span
                    key={category.id}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {getCategoryName(category)}
                    {multiple && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          removeCategory(category.id)
                        }}
                        className="ml-1 text-blue-600 hover:text-blue-800"
                      >
                        ×
                      </button>
                    )}
                  </span>
                ))
              )}
            </div>
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform ${
                isOpen ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>

        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
            <div className="p-2">
              <input
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            {isLoading ? (
              <div className="p-3 text-center text-gray-500">Loading...</div>
            ) : (
              <div className="py-1">
                {filteredCategories.length === 0 ? (
                  <div className="p-3 text-center text-gray-500">
                    No categories found
                  </div>
                ) : (
                  filteredCategories.map((category) => {
                    const isSelected = selectedCategories.some(
                      (cat) => cat.id === category.id
                    )
                    return (
                      <div
                        key={category.id}
                        className={`px-3 py-2 cursor-pointer hover:bg-gray-100 flex items-center ${
                          isSelected ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => handleCategoryToggle(category)}
                      >
                        <div className="flex items-center">
                          <span
                            className={`text-sm ${
                              isSelected
                                ? 'font-medium text-blue-900'
                                : 'text-gray-900'
                            }`}
                          >
                            {getCategoryName(category)}
                          </span>
                        </div>
                        {isSelected && (
                          <svg
                            className="w-4 h-4 ml-auto text-blue-600"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                    )
                  })
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default CategorySelector
